import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ConfigPanel from '../components/v3/ConfigPanel';
import DevViewTabs from '../components/v3/DevViewTabs';
import InspectionsList from '../components/v3/InspectionsList';
import NewInspectionForm from '../components/v3/NewInspectionForm';
import EndpointMonitor from '../components/v3/EndpointMonitor';
import { loucaApi } from '../services/loucaApi';

/**
 * Page principale InspectionV3 avec toutes les fonctionnalités
 * - Paramétrage complet
 * - Vue développeur
 * - Liste des inspections
 * - Nouvelle inspection
 * - Monitoring des endpoints
 */
const InspectionV3 = () => {
  const [config, setConfig] = useState({
    enable_v9: true,
    v9_step1_model: 'gemini-2.5-flash-lite',
    v9_step1_thinking: 'off',
    v9_step2_model: 'gemini-2.5-flash',
    v9_step2_thinking: 'on',
    enable_florence: false,
  });
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('inspections'); // inspections, config, dev

  // Polling pour les sessions en cours
  useEffect(() => {
    if (!selectedSessionId) return;

    const pollSession = async () => {
      try {
        const session = await loucaApi.getSessionV3(selectedSessionId);
        setSessionData(session);

        // Si la session est terminée, charger les résultats
        if (session.status === 'completed') {
          try {
            const results = await loucaApi.getSessionResultsV3(selectedSessionId);
            setResultData(results);
          } catch (err) {
            console.error('Error loading results:', err);
          }
        }

        // Si toujours en cours, continuer le polling
        if (session.status === 'processing') {
          setTimeout(pollSession, 3000);
        }
      } catch (err) {
        console.error('Error polling session:', err);
      }
    };

    pollSession();
  }, [selectedSessionId]);

  const handleSelectInspection = (sessionId) => {
    setSelectedSessionId(sessionId);
    setActiveTab('dev');
    setResultData(null);
    setSessionData(null);
  };

  const handleInspectionCreated = (sessionId) => {
    setSelectedSessionId(sessionId);
    setActiveTab('dev');
  };

  const consoleLogs = resultData?.console_logs || sessionData?.console_logs || [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6 pb-20">
        {/* Header */}
        <header className="bg-gradient-to-r from-orange-500/20 to-slate-800 border border-orange-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Inspection V9</p>
              <h1 className="text-3xl font-bold text-white mt-2">Workflow 2 Étapes LLM</h1>
              <p className="text-slate-300 mt-2">
                Détection rapide (Step 1) → Description détaillée (Step 2)
              </p>
            </div>
            <EndpointMonitor />
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('inspections')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'inspections'
                ? 'text-orange-400 border-b-2 border-orange-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Inspections
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'config'
                ? 'text-orange-400 border-b-2 border-orange-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Paramétrage
          </button>
          <button
            onClick={() => setActiveTab('dev')}
            className={`px-6 py-3 font-medium transition ${
              activeTab === 'dev'
                ? 'text-orange-400 border-b-2 border-orange-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Vue Développeur
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Inspections List & New Inspection */}
          {activeTab === 'inspections' && (
            <div className="lg:col-span-3 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <NewInspectionForm
                  config={config}
                  onInspectionCreated={handleInspectionCreated}
                />
                <InspectionsList
                  onSelectInspection={handleSelectInspection}
                  selectedSessionId={selectedSessionId}
                />
              </div>
            </div>
          )}

          {/* Config Tab */}
          {activeTab === 'config' && (
            <div className="lg:col-span-3">
              <ConfigPanel config={config} onConfigChange={setConfig} />
            </div>
          )}

          {/* Dev Tab */}
          {activeTab === 'dev' && (
            <>
              {selectedSessionId ? (
                <>
                  {/* Session Info */}
                  <div className="lg:col-span-1 space-y-6">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                      <h2 className="text-xl font-semibold text-white mb-4">Session</h2>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-slate-400">Session ID</p>
                          <p className="text-sm font-mono text-orange-300">{selectedSessionId}</p>
                        </div>
                        {sessionData && (
                          <>
                            <div>
                              <p className="text-xs text-slate-400">Statut</p>
                              <p className="text-sm text-white">{sessionData.status}</p>
                            </div>
                            {sessionData.created_at && (
                              <div>
                                <p className="text-xs text-slate-400">Créée le</p>
                                <p className="text-sm text-white">
                                  {new Date(sessionData.created_at).toLocaleString('fr-FR')}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedSessionId(null);
                          setResultData(null);
                          setSessionData(null);
                        }}
                        className="mt-4 w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                      >
                        Retour à la liste
                      </button>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="lg:col-span-2 space-y-6">
                    {resultData && (
                      <DevViewTabs
                        result={resultData}
                        sessionData={sessionData}
                        logDetailLevel={config?.log_detail_level || 'standard'}
                      />
                    )}
                    {!resultData && sessionData?.status === 'processing' && (
                      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                        <div className="flex items-center justify-center py-12">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                            <p className="text-white">Traitement en cours...</p>
                            <p className="text-sm text-slate-400 mt-2">
                              La session est en cours de traitement
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {!resultData && sessionData?.status === 'created' && (
                      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                        <div className="text-center py-12">
                          <p className="text-white">Session créée, en attente d'upload...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="lg:col-span-3">
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                    <div className="text-center py-12">
                      <p className="text-slate-400">
                        Sélectionnez une inspection pour voir les détails
                      </p>
                      <button
                        onClick={() => setActiveTab('inspections')}
                        className="mt-4 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                      >
                        Voir les inspections
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default InspectionV3;

